// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.
//
// Standalone read view for a single SOP Factory artifact. Fetches the artifact
// by id (GET /api/v1/sop-factory/artifacts/{id}) and renders its metadata and
// procedure steps. Linked from the SOP library card grid (Site → Procedures)
// and the alarm detail's response-procedure SOP id.
//
// Layout is ported from the design binding's `kbDetail` block: breadcrumb, a
// small id + category line, title, description, a Category/Procedure-steps
// stat row, then the numbered step list. Matching the design as-is for now: the
// step row shows only the order number and the instruction text — no verb tag,
// no gate/hold-point flag. The real artifact payload carries those, so this is
// a deliberate simplification rather than a data gap; worth revisiting once the
// design actually calls for surfacing them.

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronRight } from '@carbon/icons-react';
import { getArtifact } from '../../services/sopFactoryService';
import { useActiveSite } from '../../hooks/useActiveSite';
import { useSopSource } from '../../hooks/useSopSource';
import { Loading } from '../../components/shell/ScreenHelpers';
import type { Artifact, ArtifactStatus } from '../../types';
import './SopArtifactDetail.css';

type State =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ok'; artifact: Artifact | null };

const STATUS_COLOR: Record<string, string> = {
  approved: 'var(--green)',
  in_review: 'var(--blue)',
  draft: 'var(--helios)',
  deprecated: 'var(--text-helper)',
};

function StatusPill({ status }: { status: ArtifactStatus | undefined }) {
  if (!status) return null;
  const color = STATUS_COLOR[status] ?? 'var(--text-helper)';
  return (
    <span className="sop-tag" style={{ color, borderColor: color }}>
      {status.replace(/_/g, ' ')}
    </span>
  );
}

export default function SopArtifactDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState<State>({ status: 'loading' });
  // ADM (site-scoped) while adm-sop-catalog-enabled is on, else the global catalog.
  // The site the SOP was opened from (?site=, see sopDetailPath), else the selected one.
  // In fleet view there is no one site, so none: the catalog, not an arbitrary first site.
  const [searchParams] = useSearchParams();
  const { siteId: activeSiteId, isFleet } = useActiveSite();
  const siteId = searchParams.get('site') || (isFleet ? undefined : activeSiteId) || undefined;
  const { ready: sourceReady, source } = useSopSource(siteId);

  useEffect(() => {
    if (!id || !sourceReady) return;
    let cancelled = false;
    setState({ status: 'loading' });
    (source ? getArtifact(id, source) : getArtifact(id))
      .then(artifact => {
        if (!cancelled) setState({ status: 'ok', artifact });
      })
      .catch(err => {
        if (!cancelled) {
          setState({ status: 'error', message: (err as Error).message ?? 'Request failed' });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [id, sourceReady, source]);

  if (state.status === 'loading') return <Loading />;

  if (state.status === 'error' || !state.artifact) {
    return (
      <div className="hs-page sop-detail">
        <p>{state.status === 'error' ? state.message : 'SOP not found.'}</p>
        <button type="button" className="sop-crumb-link" onClick={() => navigate(-1)}>
          Back
        </button>
      </div>
    );
  }

  const artifact = state.artifact;
  const categories = artifact.categories ?? {};
  const steps = artifact.payload?.steps ?? [];
  const category = categories.domain ?? categories.procedure_type;

  return (
    <div className="hs-page sop-detail" data-testid="sop-detail">
      <div className="sop-crumb">
        <button type="button" className="sop-crumb-link" onClick={() => navigate(-1)}>
          Procedures
        </button>
        <ChevronRight size={12} className="sop-crumb-sep" aria-hidden="true" />
        <span className="sop-crumb-current">{artifact.artifact_id}</span>
      </div>

      <div className="sop-head">
        <div className="sop-head-line">
          <span className="sop-id" data-testid="sop-detail-id">
            {artifact.artifact_id}
          </span>
          {category && <span className="sop-chip sop-chip-category">{category}</span>}
          <StatusPill status={artifact.status} />
          {artifact.version && <span className="sop-version">v{artifact.version}</span>}
        </div>
        <h1 className="hs-title sop-title" data-testid="sop-detail-title">
          {artifact.title ?? artifact.artifact_id}
        </h1>
        {artifact.payload?.description_of_work && (
          <p className="sop-desc">{artifact.payload.description_of_work}</p>
        )}
      </div>

      <div className="sop-stats">
        <div className="sop-stat">
          <div className="sop-stat-label">Category</div>
          <div className="sop-stat-value">{category ?? '—'}</div>
        </div>
        <div className="sop-stat">
          <div className="sop-stat-label">Procedure steps</div>
          <div className="sop-stat-value">{steps.length > 0 ? `${steps.length} steps` : '—'}</div>
        </div>
      </div>

      <h2 className="sop-procedure-heading">Procedure</h2>
      {steps.length === 0 ? (
        <p className="sop-empty">No procedure steps published for this SOP.</p>
      ) : (
        <ol className="sop-steps">
          {steps.map((s, i) => (
            <li key={s.seq ?? i} data-testid="sop-step">
              <span className="sop-step-n">{s.seq ?? i + 1}</span>
              <span className="sop-step-body">
                {s.verb && <span className="sop-step-verb">{s.verb}</span>}
                <span className="sop-step-instruction">{s.instruction}</span>
              </span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
